import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fogPath, gridSegments, parseFog, type PlayerFrame } from '@realmery/realm-core';
import { fitView, usePanZoom } from '../canvas/usePanZoom.ts';
import { DrawingArt } from '../canvas/drawTool.tsx';
import { PinArt, TOKEN_RADIUS, useIconUrls } from '../canvas/pins.tsx';
import { PLAYER_CHANNEL, toPlayerView } from '../canvas/playerChannel.ts';
import { keyLabel, keysOf, useShortcut, useShortcutKeys } from '../shortcuts.ts';

/**
 * The Player View (docs/PARITY-STEP-4-PLAN.md section 9), as the desktop's `AtlasPlayerView`:
 * a window for a TV or second screen facing the table, showing one map the way players see
 * it, as the GM's window sends it - solid fog over what's unexplored, softened at its edges;
 * pins, names and drawings players may see; no notes, links or tools. Players (or the GM)
 * can pan, zoom and go full screen. It never opens the realm itself.
 */

const MIN_SCALE = 0.05;
const MAX_SCALE = 8;
/** `AtlasPlayerView.FOG` and `FOG_SOFTNESS`. */
const FOG = '#1B1C22';
const FOG_SOFTNESS = 6;

export function PlayerViewPage() {
  const [frame, setFrame] = useState<PlayerFrame | null>(null);
  const [image, setImage] = useState<{ key: string; url: string; width: number; height: number } | null>(null);
  const pz = usePanZoom(MIN_SCALE, MAX_SCALE);
  const { box, svg, size, view: v, setView, handlers, onKeyDown } = pz;
  const pictureUrl = useIconUrls();
  /** Fitting follows the window until someone pans or zooms. */
  const fitting = useRef(true);
  useShortcutKeys();

  useEffect(() => {
    document.title = 'Realmery - Player View';
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(PLAYER_CHANNEL);
    channel.onmessage = (event) => {
      const message = toPlayerView(event.data);
      if (!message) return;
      if (message.type === 'closed') {
        setFrame(null);
      } else if (message.type === 'frame') {
        setFrame(message.frame);
      } else {
        const url = URL.createObjectURL(new Blob([message.data as Uint8Array<ArrayBuffer>], { type: message.mimeType }));
        const picture = new Image();
        picture.onload = () =>
          setImage((old) => {
            if (old) URL.revokeObjectURL(old.url);
            fitting.current = true;
            return { key: message.key, url, width: picture.naturalWidth, height: picture.naturalHeight };
          });
        picture.src = url;
      }
    };
    // Ask the GM's window for the map being shown.
    channel.postMessage({ type: 'hello' });
    return () => channel.close();
  }, []);

  const shown = frame && image && image.key === frame.imageKey ? { frame, image } : null;

  const fit = useCallback(() => {
    if (!image) return;
    fitting.current = true;
    setView(fitView({ minX: 0, minY: 0, maxX: image.width, maxY: image.height }, size, 0, MIN_SCALE, MAX_SCALE));
  }, [image, size, setView]);
  useEffect(() => {
    if (fitting.current) fit();
  }, [fit]);

  const zoom = (factor: number) => {
    fitting.current = false;
    pz.zoomAt(size.width / 2, size.height / 2, factor);
  };

  const fullScreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen?.().catch(() => undefined);
  };
  useShortcut('atlas.playerFullScreen', fullScreen);
  const fullScreenKey = keysOf('atlas.playerFullScreen').map(keyLabel)[0];

  const revealed = useMemo(() => (shown ? parseFog(shown.frame.fog.revealed) : []), [shown?.frame.fog.revealed]);
  const fogD = useMemo(() => (shown && shown.frame.fog.enabled ? fogPath(true, revealed, shown.image.width, shown.image.height, FOG_SOFTNESS * 4) : ''), [shown, revealed]);
  const grid = shown ? gridSegments(shown.frame.grid, shown.image.width, shown.image.height) : [];
  const screen = (x: number, y: number) => ({ x: v.x + x * v.scale, y: v.y + y * v.scale });

  return (
    <main className="player-view" ref={box}>
      {!shown && <p className="player-view-empty">The GM hasn't shared a map yet.</p>}
      {shown && <p className="player-view-title">{shown.frame.name}</p>}
      <svg
        ref={svg}
        role="img"
        aria-label={shown ? `${shown.frame.name}, as players see it` : 'No map shared'}
        tabIndex={0}
        {...handlers}
        onPointerDown={(e) => {
          fitting.current = false;
          handlers.onPointerDown(e);
        }}
        onKeyDown={(e) => {
          fitting.current = e.key === '0';
          onKeyDown(e, fit);
        }}
      >
        <defs>
          <filter id="player-fog-soft" x="-5%" y="-5%" width="110%" height="110%">
            <feGaussianBlur stdDeviation={FOG_SOFTNESS} />
          </filter>
        </defs>
        {shown && (
          <>
            <g transform={`translate(${v.x} ${v.y}) scale(${v.scale})`}>
              <image href={shown.image.url} width={shown.image.width} height={shown.image.height} />
              {shown.frame.regions.map((r, i) => (
                <polygon key={i} points={r.points.map((p) => `${p.x},${p.y}`).join(' ')} fill={r.color} fillOpacity={0.22} stroke={r.color} strokeWidth={2} vectorEffect="non-scaling-stroke" />
              ))}
              {grid.length > 0 && (
                <path d={grid.map(([x1, y1, x2, y2]) => `M${x1},${y1}L${x2},${y2}`).join('')} fill="none" stroke={shown.frame.grid.colorHex} strokeOpacity={shown.frame.grid.opacity} strokeWidth={1} vectorEffect="non-scaling-stroke" />
              )}
              {/* The layers players may see, under the fog, so nothing drawn in unexplored parts shows through. */}
              {shown.frame.drawings.map((d) => (
                <DrawingArt key={d.id} drawing={d} />
              ))}
              {fogD && <path d={fogD} fill={FOG} fillRule="evenodd" filter="url(#player-fog-soft)" className="player-fog" />}
            </g>
            {shown.frame.regionLabels.map((l, i) => {
              const at = screen(l.at.x, l.at.y);
              return (
                <text key={i} x={at.x} y={at.y} fontSize={16} textAnchor="middle" className="atlas-label atlas-region-label">
                  {l.label}
                </text>
              );
            })}
            {shown.frame.pins.map((p, i) => {
              const at = screen(p.x, p.y);
              return (
                <g key={i} transform={`translate(${at.x} ${at.y})`} aria-label={p.label || 'Pin'}>
                  <PinArt marker={{ id: `p${i}`, style: p.style, visible: true }} look={{ color: p.color, icon: p.icon }} pictureUrl={pictureUrl} selected={false} />
                  {p.label.trim() !== '' && (
                    <text y={p.style === 'TOKEN' ? TOKEN_RADIUS + 12 : 16} fontSize={12} textAnchor="middle" className="atlas-label">
                      {p.label}
                    </text>
                  )}
                </g>
              );
            })}
          </>
        )}
      </svg>
      <div className="player-view-controls" role="group" aria-label="View">
        <button type="button" aria-label="Zoom out" onClick={() => zoom(0.8)}>
          −
        </button>
        <button type="button" aria-label="Zoom in" onClick={() => zoom(1.25)}>
          +
        </button>
        <button type="button" aria-label="Fit the map to the window" onClick={fit}>
          ⤢
        </button>
        <button type="button" aria-label="Full screen" title={fullScreenKey ? `Full screen (${fullScreenKey})` : 'Full screen'} onClick={fullScreen}>
          ⛶
        </button>
      </div>
    </main>
  );
}
